import { useEffect, useState } from 'react';
import ItemConsumptionSettings from './ItemConsumptionSettings';

const NUMERIC_FIELDS = new Set([
  'createDateYear',
  'createDateDay',
  'destroyDateYear',
  'destroyDateDay',
  'level',
  'qualityLevel',
  'magicLevel',
  'length',
  'weight',
  'height',
  'width',
  'value',
  'quantity',
  'capacity',
  'volume',
]);

const SPELL_NUMERIC_FIELDS = new Set(['quantity', 'level']);

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M5 3h11l3 3v15H5V3zm2 2v4h8V5H7zm0 14h10v-8H7v8zm2-6h6v4H9v-4z"
      fill="currentColor"
    />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M9 9h10v12H9V9zm-4-6h10v2H7v10H5V3zm4 2h8v2H9V5zm2 8v2h6v-2h-6zm0 4v2h6v-2h-6z"
      fill="currentColor"
    />
  </svg>
);

const DuplicateIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M9 9h10v10H9V9zm-4-4h10v2H7v8H5V5zm4 4V7h12v12h-2V9H9z"
      fill="currentColor"
    />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9zm1 11h8a2 2 0 0 0 2-2V8H6v10a2 2 0 0 0 2 2z"
      fill="currentColor"
    />
  </svg>
);

const createEmptyItem = () => ({
  idEntity: '',
  name: '',
  title: '',
  description: '',
  entityType: 'Item',
  createDateAge: '',
  createDateYear: '',
  createDateSeason: '',
  createDateDay: '',
  destroyDateAge: '',
  destroyDateYear: '',
  destroyDateSeason: '',
  destroyDateDay: '',
  level: '',
  itemType: '',
  qualityLevel: '',
  magicLevel: '',
  weapon: '',
  heldWithBodyPart: '',
  carriedWithBodyPart: '',
  length: 1,
  weight: 1,
  height: 1,
  width: 1,
  value: 0,
  worn: '',
  provisionMaterial: '',
  containedIn: '',
  quantity: '',
  preparedType: '',
  container: '',
  capacity: '',
  volume: '',
  general: '',
  ownerId: '',
  ownerName: '',
  materials: [],
  spells: [],
});

const getItemSubtypeKey = (itemType) => String(itemType ?? '').trim().toLowerCase();

const CONTAINER_INFERENCE_HINTS = [
  ['barrel', 'Barrel'],
  ['cask', 'Cask'],
  ['keg', 'Keg'],
  ['bucket', 'Bucket'],
  ['bottle', 'Bottle'],
  ['flask', 'Flask'],
  ['vial', 'Vial'],
  ['jug', 'Jug'],
  ['pitcher', 'Pitcher'],
  ['waterskin', 'Waterskin'],
  ['jar', 'Jar'],
  ['pot', 'Pot'],
  ['mug', 'Mug'],
  ['tankard', 'Tankard'],
  ['cup', 'Cup'],
  ['pack', 'Pack'],
  ['sack', 'Sack'],
  ['pouch', 'Pouch'],
  ['chest', 'Chest'],
  ['crate', 'Crate'],
  ['case', 'Case'],
  ['box', 'Box'],
];

const inferContainerOption = (source, containerOptions = []) => {
  const searchText = [source?.name, source?.title, source?.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!searchText) {
    return '';
  }

  const optionByLabel = new Map(
    containerOptions.map((option) => [String(option.label ?? option.value ?? '').trim().toLowerCase(), option])
  );

  for (const [needle, containerName] of CONTAINER_INFERENCE_HINTS) {
    if (!searchText.includes(needle)) {
      continue;
    }

    const match = optionByLabel.get(containerName.toLowerCase());
    if (match) {
      return match.value;
    }
  }

  return '';
};

function ItemEditor({
  title = 'Item Editor',
  copy = 'Edit the generated or loaded item directly, then save it.',
  value,
  onChange,
  onSave,
  onCopyJson,
  onDuplicate,
  onDelete,
  isSaving,
  saveMessage,
  getMasterTypeOptions,
  spellListOptions,
  spellUsageOptions,
  weaponOptions = [],
  wornOptions = [],
  materialOptions = [],
  provisionMaterialOptions = [],
  containerOptions = [],
  generalOptions = [],
  onRemoveSpell,
  ownerOptions = [],
  containedByOptions = [],
  itemRelationshipOptions = [],
  onAssignOwner,
  onAssignContainedBy,
  onAddContains,
  onRemoveContains,
  isOwnerSaving = false,
}) {
  const item = value ?? createEmptyItem();
  const itemTypeKey = getItemSubtypeKey(item.itemType);
  const [isOwnerPickerOpen, setIsOwnerPickerOpen] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [isContainedByPickerOpen, setIsContainedByPickerOpen] = useState(false);
  const [selectedContainedById, setSelectedContainedById] = useState('');
  const [isContainsPickerOpen, setIsContainsPickerOpen] = useState(false);
  const [selectedContainsId, setSelectedContainsId] = useState('');

  useEffect(() => {
    if (!isOwnerPickerOpen) {
      setSelectedOwnerId(item.ownerId ?? '');
    }
  }, [isOwnerPickerOpen, item.ownerId]);

  useEffect(() => {
    if (!isContainedByPickerOpen) {
      setSelectedContainedById(item.containedById ?? '');
    }
  }, [isContainedByPickerOpen, item.containedById]);

  const updateField = (field, nextValue) => {
    const normalizedValue = NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue;
    const nextItem = {
      ...item,
      [field]: normalizedValue,
    };

    if (field === 'itemType' && getItemSubtypeKey(normalizedValue) === 'container' && !nextItem.container) {
      nextItem.container = inferContainerOption(nextItem, containerOptions);
    }

    onChange(nextItem);
  };

  const updateSpellRow = (index, field, nextValue) => {
    onChange({
      ...item,
      spells: (item.spells ?? []).map((spell, currentIndex) =>
        currentIndex === index
          ? {
              ...spell,
              [field]:
                SPELL_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
            }
          : spell
      ),
    });
  };

  const addSpellRow = () => {
    onChange({
      ...item,
      spells: [
        ...(item.spells ?? []),
        {
          idSpell: '',
          idSpellList: '',
          idSpellUsage: '',
          quantity: '',
          level: '',
          realm: '',
          acquiredVia: '',
          acquiredNotes: '',
        },
      ],
    });
  };

  const updateMaterialRow = (index, nextValue) => {
    onChange({
      ...item,
      materials: (item.materials ?? []).map((materialRow, currentIndex) =>
        currentIndex === index ? nextValue : materialRow
      ),
    });
  };

  const addMaterialRow = () => {
    onChange({
      ...item,
      materials: [...(item.materials ?? []), ''],
    });
  };

  const removeMaterialRow = (index) => {
    onChange({
      ...item,
      materials: (item.materials ?? []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const openOwnerPicker = () => {
    setSelectedOwnerId(item.ownerId ?? '');
    setIsOwnerPickerOpen(true);
  };

  const closeOwnerPicker = () => {
    setSelectedOwnerId(item.ownerId ?? '');
    setIsOwnerPickerOpen(false);
  };

  const confirmOwnerSelection = () => {
    if (!selectedOwnerId || !onAssignOwner) {
      return;
    }

    onAssignOwner(selectedOwnerId, () => {
      setIsOwnerPickerOpen(false);
    });
  };

  const openContainedByPicker = () => {
    setSelectedContainedById(item.containedById ?? '');
    setIsContainedByPickerOpen(true);
  };

  const closeContainedByPicker = () => {
    setSelectedContainedById(item.containedById ?? '');
    setIsContainedByPickerOpen(false);
  };

  const confirmContainedBySelection = () => {
    if (!onAssignContainedBy) {
      return;
    }

    onAssignContainedBy(selectedContainedById, () => {
      setIsContainedByPickerOpen(false);
    });
  };

  const removeContainedBy = () => {
    if (!onAssignContainedBy) {
      return;
    }

    onAssignContainedBy('', () => {
      setIsContainedByPickerOpen(false);
    });
  };

  const openContainsPicker = () => {
    setSelectedContainsId('');
    setIsContainsPickerOpen(true);
  };

  const closeContainsPicker = () => {
    setSelectedContainsId('');
    setIsContainsPickerOpen(false);
  };

  const confirmContainsSelection = () => {
    if (!selectedContainsId || !onAddContains) {
      return;
    }

    onAddContains(selectedContainsId, () => {
      setSelectedContainsId('');
      setIsContainsPickerOpen(false);
    });
  };

  const availableItemRelationshipOptions = itemRelationshipOptions.filter(
    (option) => option.value !== item.idEntity
  );
  const availableContainedByOptions = containedByOptions.filter((option) => option.value !== item.idEntity);

  const renderSubtypeFields = () => {
    if (!itemTypeKey) {
      return <div className="empty-grid">Choose an item type to edit subtype-specific fields.</div>;
    }

    if (itemTypeKey === 'weapon') {
      return (
        <div className="editor-grid editor-grid-four">
          <label htmlFor="item-weapon" className="editor-field">
            <span className="field-label">Weapon</span>
            <select
              id="item-weapon"
              className="select-input"
              value={item.weapon ?? ''}
              onChange={(event) => updateField('weapon', event.target.value)}
            >
              <option value="">Select...</option>
              {weaponOptions.map((option) => (
                <option key={`item-weapon-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="item-held-body-part" className="editor-field">
            <span className="field-label">Held With</span>
            <select
              id="item-held-body-part"
              className="select-input"
              value={item.heldWithBodyPart ?? ''}
              onChange={(event) => updateField('heldWithBodyPart', event.target.value)}
            >
              <option value="">Select...</option>
              {getMasterTypeOptions('bodyPartType').map((option) => (
                <option key={`held-body-part-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="item-carried-body-part" className="editor-field">
            <span className="field-label">Carried With</span>
            <select
              id="item-carried-body-part"
              className="select-input"
              value={item.carriedWithBodyPart ?? ''}
              onChange={(event) => updateField('carriedWithBodyPart', event.target.value)}
            >
              <option value="">Select...</option>
              {getMasterTypeOptions('bodyPartType').map((option) => (
                <option key={`carried-body-part-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      );
    }

    if (itemTypeKey === 'worn') {
      return (
        <div className="editor-grid editor-grid-three">
          <label htmlFor="item-worn" className="editor-field">
            <span className="field-label">Worn</span>
            <select
              id="item-worn"
              className="select-input"
              value={item.worn ?? ''}
              onChange={(event) => updateField('worn', event.target.value)}
            >
              <option value="">Select...</option>
              {wornOptions.map((option) => (
                <option key={`item-worn-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      );
    }

    if (itemTypeKey === 'provision') {
      const preparedTypeOptions = getMasterTypeOptions('preparedType');

      return (
        <div className="editor-grid editor-grid-three">
          <label htmlFor="item-provision-material" className="editor-field">
            <span className="field-label">Provision Material</span>
            <select
              id="item-id-provision"
              className="select-input"
              value={item.provisionMaterial ?? ''}
              onChange={(event) => updateField('provisionMaterial', event.target.value)}
            >
              <option value="">Select...</option>
              {provisionMaterialOptions.map((option) => (
                <option key={`item-provision-material-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="item-quantity" className="editor-field">
            <span className="field-label">Quantity</span>
            <input
              id="item-quantity"
              className="text-input editor-input"
              type="number"
              value={item.quantity}
              onChange={(event) => updateField('quantity', event.target.value)}
            />
          </label>
          {preparedTypeOptions.length > 0 && (
            <label htmlFor="item-prepared-type" className="editor-field">
              <span className="field-label">Prepared Type</span>
              <select
                id="item-prepared-type"
                className="select-input"
                value={item.preparedType ?? ''}
                onChange={(event) => updateField('preparedType', event.target.value)}
              >
                <option value="">Select...</option>
                {preparedTypeOptions.map((option) => (
                  <option key={`prepared-type-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      );
    }

    if (itemTypeKey === 'container') {
      return (
        <div className="editor-grid editor-grid-three">
          <label htmlFor="item-container" className="editor-field">
            <span className="field-label">Container</span>
            <select
              id="item-container"
              className="select-input"
              value={item.container ?? ''}
              onChange={(event) => updateField('container', event.target.value)}
            >
              <option value="">Select...</option>
              {containerOptions.map((option) => (
                <option key={`item-container-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="item-capacity" className="editor-field">
            <span className="field-label">Capacity</span>
            <input
              id="item-capacity"
              className="text-input editor-input"
              type="number"
              min="0"
              step="1"
              value={item.capacity}
              onChange={(event) => updateField('capacity', event.target.value)}
            />
          </label>
          <label htmlFor="item-volume" className="editor-field">
            <span className="field-label">Volume</span>
            <input
              id="item-volume"
              className="text-input editor-input"
              type="number"
              min="0"
              step="any"
              value={item.volume}
              onChange={(event) => updateField('volume', event.target.value)}
            />
          </label>
          <label htmlFor="item-carried-container" className="editor-field">
            <span className="field-label">Carried With</span>
            <select
              id="item-carried-container"
              className="select-input"
              value={item.carriedWithBodyPart ?? ''}
              onChange={(event) => updateField('carriedWithBodyPart', event.target.value)}
            >
              <option value="">Select...</option>
              {getMasterTypeOptions('bodyPartType').map((option) => (
                <option key={`container-carried-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      );
    }

    if (itemTypeKey === 'general') {
      return (
        <div className="editor-grid editor-grid-two">
          <label htmlFor="item-general" className="editor-field">
            <span className="field-label">General</span>
            <select
              id="item-general"
              className="select-input"
              value={item.general ?? ''}
              onChange={(event) => updateField('general', event.target.value)}
            >
              <option value="">Select...</option>
              {generalOptions.map((option) => (
                <option key={`item-general-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      );
    }

    return <div className="empty-grid">No editor is defined for item type "{item.itemType}".</div>;
  };

  return (
    <section className="panel">
      <div className="panel-header-row">
        <div>
          <h2>{title}</h2>
          <p className="panel-copy">{copy}</p>
        </div>
        <div className="button-row compact">
          <button type="button" className="secondary-button button-with-icon" onClick={onCopyJson}>
            <CopyIcon />
            <span>JSON</span>
          </button>
          <button
            type="button"
            className="secondary-button button-with-icon"
            onClick={onDuplicate}
            disabled={!item.idEntity || isSaving}
            title={!item.idEntity ? 'Save the item before duplicating it.' : undefined}
          >
            <DuplicateIcon />
            <span>Duplicate</span>
          </button>
          {onDelete && item.idEntity && (
            <button type="button" className="secondary-button button-with-icon danger-button" onClick={onDelete} disabled={isSaving}>
              <TrashIcon />
              <span>Delete</span>
            </button>
          )}
          <button type="button" className="secondary-button button-with-icon" onClick={onSave} disabled={isSaving}>
            <SaveIcon />
            <span>{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>
      {saveMessage && <div className="panel-status">{saveMessage}</div>}

      <div className="editor-layout">
        <section className="editor-section">
          <h3>Entity</h3>
          <div className="editor-grid editor-grid-two">
            <label htmlFor="item-name" className="editor-field">
              <span className="field-label">Name</span>
              <input
                id="item-name"
                className="text-input editor-input"
                value={item.name}
                onChange={(event) => updateField('name', event.target.value)}
              />
            </label>
            <label htmlFor="item-title" className="editor-field">
              <span className="field-label">Title</span>
              <input
                id="item-title"
                className="text-input editor-input"
                value={item.title}
                onChange={(event) => updateField('title', event.target.value)}
              />
            </label>
            <label htmlFor="item-entity-type" className="editor-field">
              <span className="field-label">Entity Type</span>
              <select
                id="item-entity-type"
                className="select-input"
                value={item.entityType ?? ''}
                onChange={(event) => updateField('entityType', event.target.value)}
              >
                {getMasterTypeOptions('entityType').map((option) => (
                  <option key={`item-entity-type-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="item-level" className="editor-field">
              <span className="field-label">Level</span>
              <input
                id="item-level"
                className="text-input editor-input"
                type="number"
                value={item.level}
                onChange={(event) => updateField('level', event.target.value)}
              />
            </label>
          </div>
          <label htmlFor="item-description" className="editor-field editor-field-spaced">
            <span className="field-label">Description</span>
            <textarea
              id="item-description"
              className="text-area editor-textarea"
              value={item.description}
              onChange={(event) => updateField('description', event.target.value)}
            />
          </label>
        </section>

        <section className="editor-section">
          <h3>Timeline</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="item-create-age" className="editor-field">
              <span className="field-label">Create Age</span>
              <select
                id="item-create-age"
                className="select-input"
                value={item.createDateAge ?? ''}
                onChange={(event) => updateField('createDateAge', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('age-multiple').map((option) => (
                  <option key={`item-create-age-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="item-create-year" className="editor-field">
              <span className="field-label">Create Year</span>
              <input
                id="item-create-year"
                className="text-input editor-input"
                type="number"
                value={item.createDateYear}
                onChange={(event) => updateField('createDateYear', event.target.value)}
              />
            </label>
            <label htmlFor="item-create-season" className="editor-field">
              <span className="field-label">Create Season</span>
              <select
                id="item-create-season"
                className="select-input"
                value={item.createDateSeason ?? ''}
                onChange={(event) => updateField('createDateSeason', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('season-multiple').map((option) => (
                  <option key={`item-create-season-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="item-create-day" className="editor-field">
              <span className="field-label">Create Day</span>
              <input
                id="item-create-day"
                className="text-input editor-input"
                type="number"
                value={item.createDateDay}
                onChange={(event) => updateField('createDateDay', event.target.value)}
              />
            </label>
            <label htmlFor="item-destroy-age" className="editor-field">
              <span className="field-label">Destroy Age</span>
              <select
                id="item-destroy-age"
                className="select-input"
                value={item.destroyDateAge ?? ''}
                onChange={(event) => updateField('destroyDateAge', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('age-multiple').map((option) => (
                  <option key={`item-destroy-age-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="item-destroy-year" className="editor-field">
              <span className="field-label">Destroy Year</span>
              <input
                id="item-destroy-year"
                className="text-input editor-input"
                type="number"
                value={item.destroyDateYear}
                onChange={(event) => updateField('destroyDateYear', event.target.value)}
              />
            </label>
            <label htmlFor="item-destroy-season" className="editor-field">
              <span className="field-label">Destroy Season</span>
              <select
                id="item-destroy-season"
                className="select-input"
                value={item.destroyDateSeason ?? ''}
                onChange={(event) => updateField('destroyDateSeason', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('season-multiple').map((option) => (
                  <option key={`item-destroy-season-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="item-destroy-day" className="editor-field">
              <span className="field-label">Destroy Day</span>
              <input
                id="item-destroy-day"
                className="text-input editor-input"
                type="number"
                value={item.destroyDateDay}
                onChange={(event) => updateField('destroyDateDay', event.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="editor-section">
          <h3>Item</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="item-type" className="editor-field">
              <span className="field-label">Item Type</span>
              <select
                id="item-type"
                className="select-input"
                value={item.itemType ?? ''}
                onChange={(event) => updateField('itemType', event.target.value)}
              >
                <option value="">Select...</option>
                {getMasterTypeOptions('itemType').map((option) => (
                  <option key={`item-type-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="item-quality-level" className="editor-field">
              <span className="field-label">Quality Level</span>
              <input
                id="item-quality-level"
                className="text-input editor-input"
                type="number"
                value={item.qualityLevel}
                onChange={(event) => updateField('qualityLevel', event.target.value)}
              />
            </label>
            <label htmlFor="item-magic-level" className="editor-field">
              <span className="field-label">Magic Level</span>
              <input
                id="item-magic-level"
                className="text-input editor-input"
                type="number"
                value={item.magicLevel}
                onChange={(event) => updateField('magicLevel', event.target.value)}
              />
            </label>
            <label htmlFor="item-length" className="editor-field">
              <span className="field-label">Length</span>
              <input
                id="item-length"
                className="text-input editor-input"
                type="number"
                value={item.length}
                onChange={(event) => updateField('length', event.target.value)}
              />
            </label>
            <label htmlFor="item-width" className="editor-field">
              <span className="field-label">Width</span>
              <input
                id="item-width"
                className="text-input editor-input"
                type="number"
                value={item.width}
                onChange={(event) => updateField('width', event.target.value)}
              />
            </label>
            <label htmlFor="item-height" className="editor-field">
              <span className="field-label">Height</span>
              <input
                id="item-height"
                className="text-input editor-input"
                type="number"
                value={item.height}
                onChange={(event) => updateField('height', event.target.value)}
              />
            </label>
            <label htmlFor="item-weight" className="editor-field">
              <span className="field-label">Weight</span>
              <input
                id="item-weight"
                className="text-input editor-input"
                type="number"
                value={item.weight}
                onChange={(event) => updateField('weight', event.target.value)}
              />
            </label>
            <label htmlFor="item-value" className="editor-field">
              <span className="field-label">Value</span>
              <input
                id="item-value"
                className="text-input editor-input"
                type="number"
                value={item.value}
                onChange={(event) => updateField('value', event.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Ownership</h3>
            {!isOwnerPickerOpen && (
              <button
                type="button"
                className="secondary-button"
                onClick={openOwnerPicker}
                disabled={!item.idEntity || isOwnerSaving}
                title={!item.idEntity ? 'Save the item before assigning ownership.' : undefined}
              >
                {item.ownerId ? 'Change Owner' : 'Owned By'}
              </button>
            )}
          </div>
          {item.ownerName ? (
            <div className="panel-copy">Owned By: {item.ownerName}</div>
          ) : (
            <div className="panel-copy">
              {item.idEntity ? 'No owner assigned.' : 'Save the item before assigning ownership.'}
            </div>
          )}
          {isOwnerPickerOpen && (
            <div className="editor-grid editor-grid-two">
              <label htmlFor="item-owner" className="editor-field">
                <span className="field-label">Being</span>
                <select
                  id="item-owner"
                  className="select-input"
                  value={selectedOwnerId}
                  onChange={(event) => setSelectedOwnerId(event.target.value)}
                  disabled={isOwnerSaving}
                >
                  <option value="">Select...</option>
                  {ownerOptions.map((option) => (
                    <option key={`item-owner-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="button-row ownership-action-row">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeOwnerPicker}
                  disabled={isOwnerSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-with-icon"
                  onClick={confirmOwnerSelection}
                  disabled={!selectedOwnerId || isOwnerSaving}
                >
                  <span>{isOwnerSaving ? 'Saving...' : 'Select Owner'}</span>
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Contained By</h3>
            {!isContainedByPickerOpen && (
              <div className="button-row compact">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={openContainedByPicker}
                  disabled={!item.idEntity || isOwnerSaving}
                  title={!item.idEntity ? 'Save the item before assigning containment.' : undefined}
                >
                  {item.containedById ? 'Change Container' : 'Set Container'}
                </button>
                {item.containedById && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={removeContainedBy}
                    disabled={isOwnerSaving}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
          {item.containedByName ? (
            <div className="panel-copy">Contained By: {item.containedByName}</div>
          ) : (
            <div className="panel-copy">
              {item.idEntity ? 'No container assigned.' : 'Save the item before assigning containment.'}
            </div>
          )}
          {isContainedByPickerOpen && (
            <div className="editor-grid editor-grid-two">
              <label htmlFor="item-contained-by" className="editor-field">
                <span className="field-label">Container Item</span>
                <select
                  id="item-contained-by"
                  className="select-input"
                  value={selectedContainedById}
                  onChange={(event) => setSelectedContainedById(event.target.value)}
                  disabled={isOwnerSaving}
                >
                  <option value="">Select...</option>
                  {availableContainedByOptions.map((option) => (
                    <option key={`item-contained-by-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="button-row ownership-action-row">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeContainedByPicker}
                  disabled={isOwnerSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-with-icon"
                  onClick={confirmContainedBySelection}
                  disabled={isOwnerSaving}
                >
                  <span>{isOwnerSaving ? 'Saving...' : 'Save Container'}</span>
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Contains</h3>
            {!isContainsPickerOpen && (
              <button
                type="button"
                className="secondary-button"
                onClick={openContainsPicker}
                disabled={!item.idEntity || isOwnerSaving}
                title={!item.idEntity ? 'Save the item before adding contains relationships.' : undefined}
              >
                Add Contained Item
              </button>
            )}
          </div>
          {isContainsPickerOpen && (
            <div className="editor-grid editor-grid-two">
              <label htmlFor="item-contains" className="editor-field">
                <span className="field-label">Contained Item</span>
                <select
                  id="item-contains"
                  className="select-input"
                  value={selectedContainsId}
                  onChange={(event) => setSelectedContainsId(event.target.value)}
                  disabled={isOwnerSaving}
                >
                  <option value="">Select...</option>
                  {availableItemRelationshipOptions.map((option) => (
                    <option key={`item-contains-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="button-row ownership-action-row">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeContainsPicker}
                  disabled={isOwnerSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-with-icon"
                  onClick={confirmContainsSelection}
                  disabled={!selectedContainsId || isOwnerSaving}
                >
                  <span>{isOwnerSaving ? 'Saving...' : 'Add Item'}</span>
                </button>
              </div>
            </div>
          )}
          {(item.containsItems ?? []).length > 0 ? (
            <div className="editor-table">
              <div className="editor-table-row editor-table-head editor-grid-two">
                <span>Name</span>
                <span className="table-action-head" title="Remove">
                  <TrashIcon />
                </span>
              </div>
              {(item.containsItems ?? []).map((relatedItem) => (
                <div key={`contains-${relatedItem.idEntity}`} className="editor-table-row editor-grid-two">
                  <span>{relatedItem.title ? `${relatedItem.name} (${relatedItem.title})` : relatedItem.name}</span>
                  <button
                    type="button"
                    className="icon-button danger-button"
                    aria-label="Remove contained item"
                    disabled={isOwnerSaving}
                    onClick={() => onRemoveContains?.(relatedItem)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-grid">
              {item.idEntity ? 'This item does not contain any other items.' : 'Save the item before adding contained items.'}
            </div>
          )}
        </section>

        <section className="editor-section">
          <h3>{item.itemType ? `${item.itemType} Details` : 'Item Details'}</h3>
          {renderSubtypeFields()}
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Materials</h3>
            <button type="button" className="secondary-button button-with-icon" onClick={addMaterialRow}>
              <span>Add Material</span>
            </button>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head materials-grid">
              <span>Material</span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {(item.materials ?? []).map((materialRow, index) => (
              <div key={`item-material-${index}`} className="editor-table-row materials-grid">
                <select
                  className="select-input"
                  value={materialRow ?? ''}
                  onChange={(event) => updateMaterialRow(index, event.target.value)}
                >
                  <option value="">Select...</option>
                  {materialOptions.map((option) => (
                    <option key={`item-material-row-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove material"
                  onClick={() => removeMaterialRow(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {(item.materials ?? []).length === 0 && <div className="empty-grid">No materials yet.</div>}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Spells</h3>
            <button type="button" className="secondary-button button-with-icon" onClick={addSpellRow}>
              <span>Add Spell</span>
            </button>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head spells-grid">
              <span>Spell List</span>
              <span>Spell Usage</span>
              <span>Quantity</span>
              <span title="For Acquisition: teaching ceiling, independent of item quality and enchantment. Otherwise: usable effect level.">Teaching / Effect Level</span>
              <span>Realm</span>
              <span>Acquired Via</span>
              <span>Notes</span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {(item.spells ?? []).map((spell, index) => (
              <div key={`item-spell-${index}`} className="editor-table-row spells-grid">
                <select
                  className="select-input"
                  value={spell.idSpellList}
                  onChange={(event) => updateSpellRow(index, 'idSpellList', event.target.value)}
                >
                  <option value="">Select...</option>
                  {(spellListOptions ?? []).map((option) => (
                    <option key={`item-spell-list-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={spell.idSpellUsage}
                  onChange={(event) => updateSpellRow(index, 'idSpellUsage', event.target.value)}
                >
                  <option value="">Select...</option>
                  {(spellUsageOptions ?? []).map((option) => (
                    <option key={`item-spell-usage-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  className="text-input editor-input"
                  type="number"
                  value={spell.quantity}
                  onChange={(event) => updateSpellRow(index, 'quantity', event.target.value)}
                />
                <input
                  className="text-input editor-input"
                  type="number"
                  value={spell.level}
                  onChange={(event) => updateSpellRow(index, 'level', event.target.value)}
                />
                <select
                  className="select-input"
                  value={spell.realm}
                  onChange={(event) => updateSpellRow(index, 'realm', event.target.value)}
                >
                  <option value="">Select...</option>
                  {getMasterTypeOptions('realm').map((option) => (
                    <option key={`item-spell-realm-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={spell.acquiredVia}
                  onChange={(event) => updateSpellRow(index, 'acquiredVia', event.target.value)}
                >
                  <option value="">Select...</option>
                  {getMasterTypeOptions('acquiredVia').map((option) => (
                    <option key={`item-spell-acquired-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <textarea
                  className="text-area grid-textarea"
                  value={spell.acquiredNotes}
                  onChange={(event) => updateSpellRow(index, 'acquiredNotes', event.target.value)}
                />
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove spell"
                  onClick={() => onRemoveSpell(index, spell)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {(item.spells ?? []).length === 0 && <div className="empty-grid">No spells yet.</div>}
          </div>
        </section>
        <ItemConsumptionSettings itemId={item.idEntity} />
      </div>
    </section>
  );
}

export default ItemEditor;
